import {
  mergeWorkspacesSources,
  normalizeWorkspacesForUi,
} from "app/pages/customers/dsok/utils/normalizeDsokWorkspaces";

function parseDsokSettings(customer) {
  if (!customer?.dsokSettings) return null;
  if (typeof customer.dsokSettings === "object") return customer.dsokSettings;
  if (typeof customer.dsokSettings === "string") {
    try {
      return JSON.parse(customer.dsokSettings);
    } catch {
      return null;
    }
  }
  return null;
}

function buildActiveTicketPrintersGrouped(data, rootConnection = "") {
  if (!Array.isArray(data) || data.length === 0) return [];

  const out = [];
  data.forEach((w, i) => {
    if (!w || typeof w !== "object") return;
    const workspaceKey = String(w.uuid ?? w.id ?? i).trim() || String(i);
    const workspaceName =
      typeof w.name === "string" && w.name.trim() !== ""
        ? w.name.trim()
        : workspaceKey;
    const connectionCode =
      String(w.connectionCode ?? "").trim() || rootConnection;

    const devices = Array.isArray(w.devices) ? w.devices : [];
    const printers = devices
      .map((d, idx) => ({ d, idx }))
      .filter(({ d }) => {
        if (!d || typeof d !== "object") return false;
        const type = String(d.type ?? "")
          .trim()
          .toLowerCase();
        return type === "ticket" && d.isActive !== false;
      })
      .map(({ d, idx }) => {
        const uuid = String(d.uuid ?? "").trim();
        const localIp = String(d.localIp ?? d.ipAddress ?? "").trim();
        const name =
          typeof d.name === "string" && d.name.trim() !== ""
            ? d.name.trim()
            : uuid || `Tlačiareň ${idx + 1}`;
        return {
          deviceKey: uuid || String(idx),
          uuid,
          name,
          localIp,
        };
      });

    if (printers.length === 0) return;

    out.push({
      workspaceKey,
      workspaceName,
      connectionCode,
      printers,
    });
  });

  return out;
}

/**
 * Ticket printers grouped by workspace document — active `ticket` devices only.
 * Used for voucher bulk print modal.
 *
 * @param {object|null|undefined} doc — POS workspaces document from `/api/v1/access-system/posWorkspace`
 * @param {string} [rootConnection]
 * @returns {Array<{ workspaceKey: string, workspaceName: string, connectionCode: string, printers: Array<{ deviceKey: string, uuid: string, name: string, localIp: string }> }>}
 */
export function getActiveTicketPrintersGroupedFromWorkspacesDocument(
  doc,
  rootConnection = "",
) {
  const { data } = normalizeWorkspacesForUi(
    doc && typeof doc === "object" ? doc : {},
  );
  return buildActiveTicketPrintersGrouped(data, rootConnection);
}

/**
 * Ticket printers grouped by workspace — active `ticket` devices only.
 * Used as a legacy fallback from auth user customer dsokSettings.
 *
 * @param {object|null|undefined} customer — auth user customer with dsokSettings
 * @returns {Array<{ workspaceKey: string, workspaceName: string, connectionCode: string, printers: Array<{ deviceKey: string, uuid: string, name: string, localIp: string }> }>}
 */
export function getActiveTicketPrintersGroupedByWorkspace(customer) {
  const parsed = parseDsokSettings(customer);
  if (!parsed) return [];

  const merged = mergeWorkspacesSources(
    parsed.customer?.workspaces,
    parsed.workspaces,
  );
  if (!merged || typeof merged !== "object") return [];

  const { data } = normalizeWorkspacesForUi(merged);
  const rootConnection = String(
    parsed.customer?.dlaConnectionCode ?? "",
  ).trim();
  return buildActiveTicketPrintersGrouped(data, rootConnection);
}
